import { useCallback, useState } from 'react'
import {
  type UseSchemaValidationResult,
  useSchemaValidation,
} from '@/features/schema/hooks/useSchemaValidation'
import type { ValidationGate } from '@/services/api/schemas'
import { errorMessage } from '@/utils/errors'

export interface UseSchemaPlaygroundResult extends Omit<UseSchemaValidationResult, 'validate'> {
  parseError: string | null
  // Playground validates pasted JSON text (parsed here) rather than an object,
  // and maps the "all" gate option to an un-gated request.
  validate: (json: string, gate: ValidationGate | 'all') => Promise<void>
}

/** Schema-playground logic. Wraps the shared {@link useSchemaValidation} core
 *  with the playground-specific bits: parsing the pasted JSON text and mapping
 *  the "all severities" gate option to an un-gated request. The shared core owns
 *  the registry load, the validate call, and the findings/server-error state, so
 *  the playground and the schema detail page run validation through one hook. */
export function useSchemaPlayground(): UseSchemaPlaygroundResult {
  const core = useSchemaValidation()
  const { validate: validateSchema, reset: resetCore } = core
  const [parseError, setParseError] = useState<string | null>(null)

  const validate = useCallback(
    async (json: string, gate: ValidationGate | 'all') => {
      setParseError(null)
      let parsed: Record<string, unknown>
      try {
        parsed = JSON.parse(json) as Record<string, unknown>
      } catch (err) {
        setParseError(errorMessage(err, 'invalid JSON'))
        resetCore()
        return
      }
      await validateSchema(parsed, gate === 'all' ? undefined : gate)
    },
    [validateSchema, resetCore]
  )

  const reset = useCallback(() => {
    setParseError(null)
    resetCore()
  }, [resetCore])

  return { ...core, parseError, validate, reset }
}
